import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { productPath } from "../../seo/productSeo";
import ratkingCard from "../../assets/heroes/ratking-card.webp";
import babaCard from "../../assets/heroes/baba-card.webp";

/* Deadlock's two newest heroes and the scripts the Internal build ships for
   them. Every line here is what the shipped script does -- read from the
   headers of ratking_*.lua and baba_birds.lua -- so keep it in step with
   those files rather than with what would sound good. External is not
   mentioned: its hero scripts are a separate set, and these are not
   confirmed in it. Deadman Danny's and Solomon's lines are read from
   danny_*.lua and solomon_*.lua. */
const HEROES = [
  {
    name: "Rat King",
    art: ratkingCard,
    scripts: [
      { ability: "Rule Ratannia", tag: "Ultimate", body: "Casts by itself, only on someone the ult will finish." },
      { ability: "Scrap Grenade", body: "Thrown the moment it is up, or held for a throw that kills." },
      { ability: "Rat Swarm", body: "Sent at the nearest enemy whenever a charge is ready." },
      { ability: "Royal Pestments", body: "Armors up when your health drops with an enemy close." },
    ],
  },
  {
    name: "Baba",
    art: babaCard,
    scripts: [
      {
        ability: "Feed The Birds",
        tag: "Ultimate",
        body: "Starts only on someone it kills, keeps the bread landing on them, and releases at full stacks so the pigeons hit at once.",
      },
    ],
  },
] as const;

export default function NewHeroes() {
  return (
    <section id="new-heroes" className="relative scroll-mt-24 px-6 py-14 lg:py-20">
      <div className="relative mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="mb-10 flex flex-col gap-5 md:flex-row md:items-end md:justify-between"
        >
          <div className="max-w-xl">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent-pink/30 bg-accent-pink/10 px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-accent-pink">
              New in Deadlock
            </span>
            <h2 className="display text-5xl text-white md:text-6xl">
              <span className="whitespace-nowrap">Violet, Deadman Danny,</span>{" "}
              <span className="whitespace-nowrap">Rat King</span> and Baba,{" "}
              <span className="text-primary-light">scripted.</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/60 md:text-lg">
              Violet, Deadman Danny and Solomon just got their scripts, and the newest heroes before them already
              had theirs in Deadlock Internal. Switch them on under Heroes in the menu and tune each one there.
            </p>
            <Link
              to="/deadlock-hero-scripts"
              className="mt-3 inline-block text-sm font-semibold text-primary-light transition-colors hover:text-white"
            >
              Every Deadlock hero with a script →
            </Link>
          </div>
          <Link
            to={productPath("deadlock-external")}
            className="group inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-white/10 bg-white/5 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:border-white/20 hover:bg-white/10 md:self-auto"
          >
            See Deadlock
            <span className="inline-block transition-transform group-hover:translate-x-0.5">→</span>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
          className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-accent-pink/25 bg-accent-pink/[0.06] px-5 py-4"
        >
          <span className="rounded-full bg-accent-pink/15 px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.16em] text-accent-pink">
            Just added
          </span>
          <span className="display text-2xl text-white">Deadman Danny &amp; Solomon</span>
          <span className="text-sm text-white/60">
            Danny: Close The Deal only when it kills, Strix Salvo, Jettison Fuel and a steered dive. Solomon: Bishop,
            Knight, Rook and Queen.
          </span>
        </motion.div>

        <div className="grid gap-5 lg:grid-cols-[1.25fr_1fr]">
          {HEROES.map((hero, i) => (
            <motion.article
              key={hero.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="edge-teal relative flex flex-col overflow-hidden rounded-2xl bg-ground-2 sm:flex-row"
            >
              {/* The card art, standing in a pool of teal and fading into the
                  panel, so a chest-up portrait does not end on a hard edge. */}
              <div className="relative h-56 shrink-0 overflow-hidden sm:h-auto sm:w-44 md:w-52">
                <div
                  aria-hidden
                  className="absolute inset-0 bg-[radial-gradient(circle_at_50%_70%,rgba(45,212,191,0.28),transparent_65%)]"
                />
                <img
                  src={hero.art}
                  width={280}
                  height={380}
                  alt={`${hero.name}, Deadlock hero`}
                  loading="lazy"
                  decoding="async"
                  className="relative h-full w-full object-cover object-[50%_35%] sm:object-top"
                />
                <div
                  aria-hidden
                  className="absolute inset-x-0 bottom-0 h-1/3 bg-linear-to-t from-ground-2 to-transparent sm:inset-y-0 sm:left-auto sm:right-0 sm:h-auto sm:w-1/3 sm:bg-linear-to-l"
                />
              </div>

              <div className="flex-1 p-5 sm:p-6">
                <div className="mb-4 flex items-baseline justify-between gap-3">
                  <h3 className="display text-3xl text-white">{hero.name}</h3>
                  <span className="text-xs text-white/40">
                    {hero.scripts.length} script{hero.scripts.length === 1 ? "" : "s"}
                  </span>
                </div>
                <ul className="space-y-3.5">
                  {hero.scripts.map((s) => (
                    <li key={s.ability} className="border-l-2 border-primary/40 pl-3">
                      <span className="flex items-center gap-2 text-sm font-semibold text-white">
                        {s.ability}
                        {"tag" in s && (
                          <span className="rounded-md bg-primary/15 px-1.5 py-px text-[0.62rem] font-bold uppercase tracking-wider text-primary-light">
                            {s.tag}
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block text-sm leading-relaxed text-white/55">{s.body}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
